// src/utils/assignmentUtils.js

/**
 * Get assignment status based solely on dates.
 * @param {Object} assignment - The assignment object with startDate and endDate
 * @param {Date} currentDate - Optional date to use for comparison (defaults to now)
 * @returns {Object} Status object with display status and other metadata
 */
export const getAssignmentStatus = (assignment, currentDate = new Date()) => {
  if (!assignment.startDate) {
    return {
      status: 'Draft',
      displayClass: 'draft',
      isAvailable: false,
      description: 'Not visible to learners'
    };
  }

  const startDate = assignment.startDate instanceof Date
    ? assignment.startDate
    : new Date(assignment.startDate);

  const endDate = assignment.endDate instanceof Date
    ? assignment.endDate
    : (assignment.endDate ? new Date(assignment.endDate) : null);

  if (startDate > currentDate) {
    return {
      status: 'Upcoming',
      displayClass: 'upcoming',
      isAvailable: false,
      description: 'Not yet open for submissions',
      startDate,
      endDate
    };
  }

  if (endDate && endDate < currentDate) {
    return {
      status: 'Closed',
      displayClass: 'closed',
      isAvailable: false,
      description: 'No longer accepting submissions',
      startDate,
      endDate
    };
  }

  return {
    status: 'Open',
    displayClass: 'open',
    isAvailable: true,
    description: 'Currently accepting submissions',
    startDate,
    endDate
  };
};

/**
 * Get the appropriate CSS class for an assignment status.
 * @param {string} status - The assignment status string
 * @returns {string} The CSS class to use
 */
export const getStatusClass = (status) => {
  if (!status) return '';

  const statusMap = {
    'draft': 'status-draft',
    'not scheduled': 'status-not-scheduled',
    'upcoming': 'status-upcoming',
    'open': 'status-open',
    'closed': 'status-closed'
  };

  return statusMap[status.toLowerCase()] || 'status-not-scheduled';
};

/**
 * Format a date from any source into a consistent format.
 * @param {Date|string|Object} date - The date to format
 * @param {string} outputFormat - 'date-object', 'iso', 'local', or 'locale-date'
 * @returns {Date|string|null} Formatted date or null if invalid
 */
export const formatDate = (date, outputFormat = 'locale-date') => {
  if (!date) return null;

  let dateObj;

  try {
    if (date && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string' && date.includes(' at ')) {
      dateObj = new Date(date.split(' at ')[0]);
    } else {
      dateObj = new Date(date);
    }

    if (isNaN(dateObj.getTime())) return null;

    if (outputFormat === 'date-object') return dateObj;
    if (outputFormat === 'iso') return dateObj.toISOString();
    if (outputFormat === 'local') return dateObj.toLocaleString();
    return dateObj.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return null;
  }
};